import * as Sentry from '@sentry/react';
import { HomeyAPI, type DiscoveryResult } from 'homey-api';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { isAbortError, throwIfAborted } from '../lib/abort';
import { getErrorMessage, hasStatusCode } from '../lib/errors';
import { SCOPES } from '../lib/scopes';
import { ToastManager } from '../ToastManager';

import { authStore } from './AuthStore';
import { BaseStore } from './BaseStore';
import { HomeyUtils } from './HomeyUtils';
import {
  type CloudApiHomey,
  isAuthCapableSelectedHomey,
  type ConnectionStatus,
  type LocalApiHomey,
  type HomeyConnectionTarget,
  type HomeyInfo,
  type HomeyFeatures,
  type HomeyReconnectSource,
  type HomeyScopes,
  type TransportType,
} from './SelectedHomeyStore.types';

export interface SelectedHomeyStoreState {
  selectedHomey: HomeyInfo | null;
  baseUrl: string | null;
  apiSilent: boolean;
  api: HomeyAPI | null;
  features: HomeyFeatures | null;
  loading: boolean;
  error: unknown;
  isConnected: boolean | null;
  isRebooting: boolean | null;
  isUpdating: boolean | null;
  bootId: string | null;
  connectionStatus: ConnectionStatus;
  reconnectAttempt: number;
  connectionEpoch: number;
  disconnectStartedAt: number | null;
  lastConnectedAt: number | null;
  lastRecoveredAt: number | null;
  lastDisconnectDurationMs: number;
  strategyId: string | null;
  transportType: TransportType;
  scopes: HomeyScopes;
  language: string | null;
}

export class SelectedHomeyStore extends BaseStore<SelectedHomeyStoreState> {
  static RECONNECT_DELAY_MS = 5000;
  static RECOVERED_INDICATOR_DURATION_MS = 15 * 1000;
  static VISIBLE_RECONNECT_FAILURE_COUNT = 2;
  static REDISCOVERY_RECONNECT_FAILURE_COUNT = 4;

  connectionSessionId = 0;
  currentReconnectSource: HomeyReconnectSource | null = null;

  reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  recoveredStateTimeout: ReturnType<typeof setTimeout> | null = null;

  activeDisconnectBinding: {
    api: HomeyAPI;
    listener: (...args: unknown[]) => void;
  } | null = null;

  abortController: AbortController | null = null;

  store = createWithEqualityFn<SelectedHomeyStoreState>()(
    devtools(
      subscribeWithSelector(() => this.createInitialState()),
      { name: 'homey' },
    ),
    shallow,
  );

  createInitialState(): SelectedHomeyStoreState {
    return {
      selectedHomey: null,
      baseUrl: null,
      apiSilent: false,
      api: null,
      features: null,
      loading: true,
      error: null,
      isConnected: null,
      isRebooting: null,
      isUpdating: null,
      bootId: null,
      connectionStatus: 'idle',
      reconnectAttempt: 0,
      connectionEpoch: 0,
      disconnectStartedAt: null,
      lastConnectedAt: null,
      lastRecoveredAt: null,
      lastDisconnectDurationMs: 0,
      strategyId: null,
      transportType: null,
      scopes: {},
      language: null,
    };
  }

  createConnectingState({
    selectedHomey,
  }: {
    selectedHomey: HomeyInfo;
  }): Partial<SelectedHomeyStoreState> {
    return {
      selectedHomey,
      api: null,
      features: null,
      loading: true,
      error: null,
      isConnected: false,
      isRebooting: null,
      isUpdating: null,
      bootId: null,
      connectionStatus: 'connecting',
      reconnectAttempt: 0,
      connectionEpoch: 0,
      disconnectStartedAt: null,
      lastConnectedAt: null,
      lastRecoveredAt: null,
      lastDisconnectDurationMs: 0,
      strategyId: null,
      transportType: null,
      scopes: {},
      language: selectedHomey.language ?? null,
    };
  }

  createConnectedState({
    selectedHomey,
    api,
    scopes,
    baseUrl,
    connectedAt,
    strategyId,
    transportType,
  }: {
    selectedHomey: HomeyInfo;
    api: HomeyAPI;
    scopes: HomeyScopes;
    baseUrl: string;
    connectedAt: number;
    strategyId: string;
    transportType: TransportType;
  }): Partial<SelectedHomeyStoreState> {
    return {
      selectedHomey,
      api,
      features: SelectedHomeyStore.getFeatures({ api, homey: selectedHomey }),
      apiSilent: false,
      scopes,
      baseUrl,
      loading: false,
      error: null,
      isConnected: true,
      isRebooting: null,
      isUpdating: null,
      connectionStatus: 'connected',
      reconnectAttempt: 0,
      disconnectStartedAt: null,
      lastConnectedAt: connectedAt,
      lastRecoveredAt: null,
      lastDisconnectDurationMs: 0,
      strategyId,
      transportType,
    };
  }

  createDisconnectedState({
    selectedHomey,
    error,
    reconnectAttempt,
    disconnectStartedAt,
  }: {
    selectedHomey: HomeyInfo;
    error: unknown;
    reconnectAttempt: number;
    disconnectStartedAt: number | null;
  }): Partial<SelectedHomeyStoreState> {
    return {
      selectedHomey,
      baseUrl: null,
      api: null,
      features: null,
      apiSilent: false,
      scopes: {},
      loading: false,
      error,
      isConnected: null,
      isRebooting: null,
      isUpdating: null,
      connectionStatus: 'disconnected',
      reconnectAttempt,
      disconnectStartedAt,
      lastDisconnectDurationMs: 0,
      strategyId: null,
      transportType: null,
    };
  }

  createReconnectPendingState({
    disconnectedAt,
  }: {
    disconnectedAt: number;
  }): Partial<SelectedHomeyStoreState> {
    return {
      isConnected: false,
      error: null,
      connectionStatus: 'reconnecting',
      reconnectAttempt: 0,
      disconnectStartedAt: disconnectedAt,
      lastDisconnectDurationMs: 0,
    };
  }

  createRecoveredState({
    selectedHomey,
    api,
    scopes,
    baseUrl,
    connectedAt,
    connectionEpoch,
    disconnectDurationMs,
    strategyId,
    transportType,
  }: {
    selectedHomey: HomeyInfo;
    api: HomeyAPI;
    scopes: HomeyScopes;
    baseUrl: string;
    connectedAt: number;
    connectionEpoch: number;
    disconnectDurationMs: number;
    strategyId: string | null;
    transportType: TransportType;
  }): Partial<SelectedHomeyStoreState> {
    const currentHomey = this.get().selectedHomey;

    if (currentHomey?.id === selectedHomey.id) {
      selectedHomey = currentHomey;
    }

    return {
      selectedHomey,
      api,
      features: SelectedHomeyStore.getFeatures({ api, homey: selectedHomey }),
      apiSilent: true,
      scopes,
      baseUrl,
      loading: false,
      error: null,
      isConnected: true,
      isRebooting: null,
      isUpdating: null,
      connectionStatus: 'recovered',
      reconnectAttempt: 0,
      connectionEpoch,
      disconnectStartedAt: null,
      lastConnectedAt: connectedAt,
      lastRecoveredAt: connectedAt,
      lastDisconnectDurationMs: disconnectDurationMs,
      strategyId,
      transportType,
    };
  }

  createReconnectAttemptState({
    reconnectAttempt,
    disconnectStartedAt,
    connectionStatus,
  }: {
    reconnectAttempt: number;
    disconnectStartedAt: number | null;
    connectionStatus: ConnectionStatus;
  }): Partial<SelectedHomeyStoreState> {
    return {
      isConnected: false,
      error: null,
      connectionStatus,
      reconnectAttempt,
      disconnectStartedAt,
      lastDisconnectDurationMs: 0,
    };
  }

  async createApi(homey: HomeyConnectionTarget): Promise<HomeyAPI> {
    if (import.meta.env.MODE === 'self-hosted') {
      const token = sessionStorage.getItem('HOMEY_LOCAL_TOKEN');

      if (token == null) {
        throw new Error('Missing Homey local token.');
      }

      try {
        return await HomeyAPI.createLocalAPI({
          address: getLocalBaseUrl(),
          token,
          debug: null,
        });
      } catch (error) {
        if (hasStatusCode(error) && error.statusCode === 401) {
          sessionStorage.removeItem('HOMEY_LOCAL_TOKEN');
          authStore.logout().catch(() => {});
        }

        throw error;
      }
    }

    if (isAuthCapableSelectedHomey(homey) === false) {
      throw new Error('Missing Homey authentication capability.');
    }

    const api = await homey.authenticate({
      reconnect: false,
    });

    return api;
  }

  async connectToCloudApiHomey(homey: CloudApiHomey) {
    const { signal, connectionSessionId } = this.startConnectionAttempt();

    await this.connectToResolvedHomey({
      signal,
      connectionSessionId,
      homey,
      reconnectSource: {
        kind: 'cloud',
        homey,
      },
    });
  }

  async connectToLocalApiHomey() {
    const { signal, connectionSessionId } = this.startConnectionAttempt();

    try {
      const homey = await this.resolveLocalApiHomey({ signal });
      throwIfAborted(signal);

      await this.connectToResolvedHomey({
        signal,
        connectionSessionId,
        homey,
        reconnectSource: {
          kind: 'local',
        },
      });
    } catch (error: unknown) {
      if (signal.aborted || isAbortError(error)) {
        return;
      }

      throw error;
    }
  }

  async connectToResolvedHomey({
    signal,
    connectionSessionId,
    homey,
    reconnectSource,
  }: {
    signal: AbortSignal;
    connectionSessionId: number;
    homey: HomeyConnectionTarget;
    reconnectSource: HomeyReconnectSource;
  }) {
    this.clearRecoveredStateTimeout();
    this.clearReconnectTimeout();
    this.clearDisconnectBinding();
    this.destroyApi();

    this.currentReconnectSource = reconnectSource;
    const nextSelectedHomey = getHomeyInfo(homey);

    try {
      this.set(this.createConnectingState({ selectedHomey: nextSelectedHomey }));

      const api = await this.createApi(homey);
      throwIfAborted(signal);

      const { baseUrl, scopes, strategyId, transportType } = await this.initializeAndConnectApi({
        signal,
        api,
        homey: nextSelectedHomey,
        connectionSessionId,
        reason: 'initial-connect',
      });
      throwIfAborted(signal);

      this.set(
        this.createConnectedState({
          selectedHomey: nextSelectedHomey,
          api,
          scopes,
          baseUrl,
          connectedAt: Date.now(),
          strategyId,
          transportType,
        }),
      );
    } catch (error: unknown) {
      if (signal.aborted || isAbortError(error)) {
        return;
      }

      this.set(
        this.createDisconnectedState({
          selectedHomey: nextSelectedHomey,
          error,
          reconnectAttempt: 0,
          disconnectStartedAt: null,
        }),
      );
    }
  }

  async resolveLocalApiHomey({ signal }: { signal: AbortSignal }): Promise<LocalApiHomey> {
    const response = await fetch(`${getLocalBaseUrl()}/api/manager/system/ping`, {
      signal,
    });

    if (response.headers.has('X-Homey-ID') === false) {
      throw new Error(`No Homey Found At Address: ${getLocalBaseUrl()}`);
    }

    if (response.headers.has('X-Homey-Version') === false) {
      throw new Error(`No Homey Version Found At Address: ${getLocalBaseUrl()}`);
    }

    return {
      id: response.headers.get('X-Homey-ID') ?? '',
      licenses: [],
      platform: 'local',
      platformVersion: 2,
      apiVersion: 3,
      softwareVersion: response.headers.get('X-Homey-Version') ?? '0.0.0',
      language: null,
      model: null,
      name: null,
      region: null,
    };
  }

  async initializeAndConnectApi({
    signal,
    api,
    homey,
    connectionSessionId,
    reason,
    reconnectAttempt = 0,
    rediscoverBaseUrl = false,
  }: {
    signal: AbortSignal;
    api: HomeyAPI;
    homey: HomeyInfo;
    connectionSessionId: number;
    reason: 'initial-connect' | 'reconnect' | 'reconnect-rediscover';
    reconnectAttempt?: number;
    rediscoverBaseUrl?: boolean;
  }): Promise<{
    baseUrl: string;
    scopes: HomeyScopes;
    strategyId: string;
    transportType: TransportType;
  }> {
    let discoveryResult: DiscoveryResult | null = null;

    if (rediscoverBaseUrl === true && shouldRediscoverBaseUrl(api) === true) {
      await api.disconnect().catch(() => {});
      discoveryResult = await api.discoverBaseUrl();
    }

    await api.connect();
    throwIfAborted(signal);

    this.bindDisconnectListener({ api, homey, signal, connectionSessionId });

    const baseUrl = discoveryResult?.baseUrl ?? (await api.baseUrl) ?? '';
    throwIfAborted(signal);

    api.i18n
      .getOptionLanguage()
      .then((result) => {
        if (signal.aborted) {
          return;
        }

        if (this.get().language !== result.value) {
          this.set({
            language: result.value,
          });
        }
      })
      .catch((error: unknown) => {
        if (signal.aborted) {
          return;
        }

        ToastManager.handleError(error);
      });

    api.system
      .getInfo()
      .then((info) => {
        if (signal.aborted) {
          return;
        }

        this.set({
          bootId: info.bootId,
        });
      })
      .catch((error: unknown) => {
        if (signal.aborted) {
          return;
        }

        console.error(error);
      });

    const scopes: HomeyScopes = {};

    for (const scope of Object.values(SCOPES)) {
      scopes[scope] = api.hasScope(scope);
    }

    Sentry.setContext('homey', {
      id: homey.id,
      softwareVersion: homey.softwareVersion,
      apiVersion: homey.apiVersion,
      platform: homey.platform,
      platformVersion: homey.platformVersion,
      model: homey.model,
      name: homey.name,
      region: homey.region,
    });

    Sentry.setTag('softwareVersion', homey.softwareVersion);
    Sentry.setTag('apiVersion', String(homey.apiVersion));
    Sentry.setTag('platform', homey.platform);
    Sentry.setTag('platformVersion', String(homey.platformVersion));

    document.title = homey.name ?? 'My Homey';

    const selectedStrategyId = discoveryResult?.strategyId ?? api.strategyId ?? 'unknown';

    return {
      baseUrl,
      scopes,
      strategyId: selectedStrategyId,
      transportType: getTransportType(selectedStrategyId),
    };
  }

  onDisconnect({
    api,
    signal,
    homey,
    connectionSessionId,
  }: {
    api: HomeyAPI;
    signal: AbortSignal;
    homey: HomeyInfo;
    connectionSessionId: number;
  }) {
    const disconnectedAt = Date.now();

    this.clearRecoveredStateTimeout();
    this.clearReconnectTimeout();

    this.set(this.createReconnectPendingState({ disconnectedAt }));

    this.reconnect({
      api,
      signal,
      homey,
      attempt: 1,
      connectionSessionId,
      disconnectedAt,
    });
  }

  reconnect({
    api,
    signal,
    homey,
    attempt = 1,
    connectionSessionId,
    disconnectedAt,
  }: {
    api: HomeyAPI;
    signal: AbortSignal;
    homey: HomeyInfo;
    attempt?: number;
    connectionSessionId: number;
    disconnectedAt: number;
  }) {
    if (signal.aborted === true) {
      return;
    }

    this.clearReconnectTimeout();
    this.reconnectTimeout = setTimeout(async () => {
      try {
        throwIfAborted(signal);

        if (api == null) {
          throw new Error('Missing Api');
        }

        const rediscoverBaseUrl =
          attempt > SelectedHomeyStore.REDISCOVERY_RECONNECT_FAILURE_COUNT &&
          shouldRediscoverBaseUrl(api);

        const { baseUrl, scopes, strategyId, transportType } = await this.initializeAndConnectApi({
          signal,
          api,
          homey,
          connectionSessionId,
          reason: rediscoverBaseUrl === true ? 'reconnect-rediscover' : 'reconnect',
          reconnectAttempt: attempt,
          rediscoverBaseUrl,
        });
        throwIfAborted(signal);

        const connectedAt = Date.now();
        const disconnectDurationMs = disconnectedAt == null ? 0 : connectedAt - disconnectedAt;
        const connectionEpoch = this.get().connectionEpoch + 1;

        this.set(
          this.createRecoveredState({
            selectedHomey: homey,
            api,
            scopes,
            baseUrl,
            connectedAt,
            connectionEpoch,
            disconnectDurationMs,
            strategyId,
            transportType,
          }),
        );

        this.scheduleRecoveredStateTimeout(connectedAt);
      } catch (error: unknown) {
        if (signal.aborted || isAbortError(error)) {
          return;
        }

        if (getErrorMessage(error) === 'Subscription Inactive') {
          this.set(
            this.createDisconnectedState({
              selectedHomey: homey,
              error,
              reconnectAttempt: attempt,
              disconnectStartedAt: disconnectedAt ?? this.get().disconnectStartedAt,
            }),
          );
          return;
        }

        const nextStatus: ConnectionStatus =
          attempt >= SelectedHomeyStore.VISIBLE_RECONNECT_FAILURE_COUNT
            ? 'disconnected'
            : 'reconnecting';

        this.set(
          this.createReconnectAttemptState({
            reconnectAttempt: attempt,
            disconnectStartedAt: disconnectedAt ?? this.get().disconnectStartedAt,
            connectionStatus: nextStatus,
          }),
        );

        this.reconnect({
          api,
          signal,
          homey,
          attempt: attempt + 1,
          connectionSessionId,
          disconnectedAt,
        });
      }
    }, SelectedHomeyStore.RECONNECT_DELAY_MS);
  }

  retry() {
    const reconnectSource = this.currentReconnectSource;

    if (reconnectSource == null) {
      return;
    }

    if (reconnectSource.kind === 'local') {
      this.connectToLocalApiHomey().catch(console.error);
      return;
    }

    this.connectToCloudApiHomey(reconnectSource.homey).catch(console.error);
  }

  reset() {
    this.clearRecoveredStateTimeout();
    this.clearReconnectTimeout();
    this.clearDisconnectBinding();
    this.abortController?.abort();
    this.abortController = null;
    this.destroyApi();
    this.currentReconnectSource = null;
    this.set(this.createInitialState());
  }

  startConnectionAttempt(): {
    signal: AbortSignal;
    connectionSessionId: number;
  } {
    if (this.abortController != null) {
      this.abortController.abort();
    }

    const controller = new AbortController();

    this.abortController = controller;
    this.connectionSessionId += 1;

    return {
      signal: controller.signal,
      connectionSessionId: this.connectionSessionId,
    };
  }

  scheduleRecoveredStateTimeout(lastRecoveredAt: number) {
    this.clearRecoveredStateTimeout();

    this.recoveredStateTimeout = setTimeout(() => {
      const state = this.get();

      if (state.lastRecoveredAt !== lastRecoveredAt) {
        return;
      }

      if (state.connectionStatus !== 'recovered') {
        return;
      }

      this.set({
        connectionStatus: 'connected',
      });
    }, SelectedHomeyStore.RECOVERED_INDICATOR_DURATION_MS);
  }

  destroyApi() {
    const state = this.get();

    this.clearDisconnectBinding();
    this.clearReconnectTimeout();

    if (state.api != null) {
      state.api.destroy();
    }
  }

  static getFeatures({ api, homey }: { api: HomeyAPI; homey: HomeyInfo }): HomeyFeatures {
    const isCloud = HomeyAPI.isHomeyAPIV3Cloud(api);
    const isCloudPremium = isCloud && api.tier === 'premium';

    return {
      isCloudPremium,
      advancedFlow: {
        hasLicense: isCloudPremium || HomeyUtils.getHasAdvancedFlowLicense(homey),
        hasVersion: isCloud || HomeyUtils.getHasAdvancedFlowVersion(homey),
      },
      moods: {
        hasMoods: HomeyUtils.getHasMoods(homey),
      },
      useSeamlessDevicePairingScreens: HomeyUtils.getIsSeamlessDevicePairingScreens(homey),
    };
  }

  assignLicences({ licenses }: { licenses: string[] }) {
    const { api, selectedHomey } = this.get();

    if (selectedHomey == null) {
      return;
    }

    const homey = { ...selectedHomey, licenses: [...licenses] };

    this.set({
      selectedHomey: homey,
      features: api == null ? null : SelectedHomeyStore.getFeatures({ api, homey }),
    });
  }

  assignAdvancedFlowLicense() {
    const { selectedHomey } = this.get();

    if (selectedHomey == null || selectedHomey.licenses.includes('advanced-flow')) {
      return;
    }

    this.assignLicences({ licenses: [...selectedHomey.licenses, 'advanced-flow'] });
  }

  async reboot() {
    const { api } = this.get();

    if (api != null) {
      api.removeAllListeners('disconnect');
      const { bootId } = await api.system.reboot();

      this.set({
        isRebooting: true,
        bootId,
      });
    }
  }

  awaitReboot({ bootId }: { bootId: string }) {
    const { api } = this.get();

    if (api != null) {
      api.removeAllListeners('disconnect');
      this.set({
        isRebooting: true,
        bootId,
      });
    }
  }

  setIsUpdating(value: boolean | null) {
    this.set({
      isUpdating: value,
    });
  }

  getApi() {
    const state = this.get();
    return state.api;
  }

  clearReconnectTimeout() {
    if (this.reconnectTimeout != null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  clearRecoveredStateTimeout() {
    if (this.recoveredStateTimeout != null) {
      clearTimeout(this.recoveredStateTimeout);
      this.recoveredStateTimeout = null;
    }
  }

  clearDisconnectBinding() {
    if (this.activeDisconnectBinding == null) {
      return;
    }

    this.activeDisconnectBinding.api.off('disconnect', this.activeDisconnectBinding.listener);
    this.activeDisconnectBinding = null;
  }

  bindDisconnectListener({
    api,
    homey,
    signal,
    connectionSessionId,
  }: {
    api: HomeyAPI;
    signal: AbortSignal;
    homey: HomeyInfo;
    connectionSessionId: number;
  }) {
    this.clearDisconnectBinding();

    const listener = (...args: unknown[]) => {
      this.onDisconnect({ api, homey, signal, connectionSessionId });
    };

    api.on('disconnect', listener);
    this.activeDisconnectBinding = {
      api,
      listener,
    };
  }
}

export function getLocalBaseUrl() {
  return import.meta.env.VITE_HOMEY_LOCAL_ADDRESS ?? window.location.origin;
}

export const selectedHomeyStore = new SelectedHomeyStore();

function shouldRediscoverBaseUrl(
  api: HomeyAPI,
): api is HomeyAPI & { discoverBaseUrl: () => Promise<DiscoveryResult> } {
  return typeof api.discoverBaseUrl === 'function';
}

function getTransportType(strategyId: string | null | undefined): TransportType {
  switch (String(strategyId ?? '').toLowerCase()) {
    case 'localsecure':
    case 'local':
    case 'mdns':
      return 'local';
    case 'cloud':
      return 'relayed';
    case 'remoteforwarded':
      return 'direct';
    default:
      return null;
  }
}

function getHomeyInfo(homey: HomeyConnectionTarget): HomeyInfo {
  return {
    id: homey.id,
    licenses: homey.licenses ?? [],
    platform: homey.platform ?? 'local',
    platformVersion: homey.platformVersion ?? 1,
    apiVersion: homey.apiVersion,
    softwareVersion: getSoftwareVersion(homey.softwareVersion),
    language: homey.language ?? null,
    model: homey.model ?? null,
    name: homey.name ?? null,
    region: homey.region ?? null,
  };
}

function getSoftwareVersion(softwareVersion: string | null | undefined): string {
  return softwareVersion ?? '0.0.0';
}
